import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Save, X, Upload, Image } from 'lucide-react';
import { categories } from '../mock';
import { productsAPI, productionItemsAPI } from '../services/api';
import { toast } from 'sonner';
import './Admin.css';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('products'); // 'products' or 'production'
  const [products, setProducts] = useState([]);
  const [productionItems, setProductionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingProductionItem, setEditingProductionItem] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showProductionForm, setShowProductionForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'textile',
    price: '',
    description: '',
    images: [],
    featured: false,
    min_quantity: '1',
    quantity_available: '0',
    dimensions: ''
  });
  const [productionFormData, setProductionFormData] = useState({
    name: '',
    description: '',
    dimensions: '',
    min_quantity: '1',
    price: '',
    images: [],
    featured: false
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingProductImages, setUploadingProductImages] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [dragActiveProduct, setDragActiveProduct] = useState(false);
  const fileInputRef = useRef(null);
  const productFileInputRef = useRef(null);

  // Load products on mount
  useEffect(() => {
    if (activeTab === 'products') {
      loadProducts();
    } else if (activeTab === 'production') {
      loadProductionItems();
    }
  }, [activeTab]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productsAPI.getAll();
      setProducts(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des produits');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadProductionItems = async () => {
    try {
      setLoading(true);
      const data = await productionItemsAPI.getAll();
      setProductionItems(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des produits de production');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier qu'il y a au moins une image
    if (!formData.images || formData.images.length === 0) {
      toast.error('Veuillez ajouter au moins une image pour le produit');
      return;
    }
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        min_quantity: parseInt(formData.min_quantity) || 1,
        quantity_available: parseInt(formData.quantity_available) || 0
      };

      if (isEditing) {
        // Update existing product
        const updated = await productsAPI.update(editingProduct.id, productData);
        setProducts(products.map(p => p.id === editingProduct.id ? updated : p));
        toast.success('Produit modifié avec succès !');
      } else {
        // Add new product
        const newProduct = await productsAPI.create(productData);
        setProducts([...products, newProduct]);
        toast.success('Produit ajouté avec succès !');
      }
      
      resetForm();
    } catch (error) {
      toast.error(isEditing ? 'Erreur lors de la modification' : 'Erreur lors de l\'ajout');
      console.error(error);
    }
  };

  const handleEdit = (product) => {
    setIsEditing(true);
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      price: product.price.toString(),
      description: product.description,
      images: product.images || (product.image ? [product.image] : []),
      featured: product.featured,
      min_quantity: product.min_quantity?.toString() || '1',
      quantity_available: product.quantity_available?.toString() || '0',
      dimensions: product.dimensions || ''
    });
    setShowProductForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await productsAPI.delete(id);
        setProducts(products.filter(p => p.id !== id));
        toast.success('Produit supprimé avec succès !');
      } catch (error) {
        toast.error('Erreur lors de la suppression');
        console.error(error);
      }
    }
  };

  // Production Items handlers
  const handleProductionInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductionFormData({
      ...productionFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Image upload handlers
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      throw new Error('Erreur lors de l\'upload');
    }
    
    const data = await response.json();
    return data.url;
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingImages(true);
    const newImages = [...productionFormData.images];
    
    try {
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} n'est pas une image valide`);
          continue;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} est trop volumineux (max 5MB)`);
          continue;
        }
        
        toast.info(`Upload de ${file.name}...`);
        const imageUrl = await uploadImage(file);
        newImages.push(imageUrl);
        toast.success(`${file.name} uploadé !`);
      }
      
      setProductionFormData({
        ...productionFormData,
        images: newImages
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload des images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleImageUpload(Array.from(e.target.files));
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = productionFormData.images.filter((_, i) => i !== index);
    setProductionFormData({
      ...productionFormData,
      images: newImages
    });
  };

  // Product (En Stock) image upload handlers
  const handleProductImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingProductImages(true);
    const newImages = [...formData.images];
    
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} n'est pas une image valide`);
          continue;
        }
        
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} est trop volumineux (max 5MB)`);
          continue;
        }
        
        toast.info(`Upload de ${file.name}...`);
        const imageUrl = await uploadImage(file);
        newImages.push(imageUrl);
        toast.success(`${file.name} uploadé !`);
      }
      
      setFormData({
        ...formData,
        images: newImages
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload des images');
    } finally {
      setUploadingProductImages(false);
    }
  };

  const handleProductDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActiveProduct(true);
    } else if (e.type === 'dragleave') {
      setDragActiveProduct(false);
    }
  };

  const handleProductDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveProduct(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleProductImageUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleProductFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleProductImageUpload(Array.from(e.target.files));
    }
  };

  const handleRemoveProductImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      images: newImages
    });
  };

  const handleProductionSubmit = async (e) => {
    e.preventDefault();
    
    if (productionFormData.images.length === 0) {
      toast.error('Veuillez ajouter au moins une image');
      return;
    }
    
    try {
      const itemData = {
        ...productionFormData,
        price: parseFloat(productionFormData.price),
        min_quantity: parseInt(productionFormData.min_quantity) || 1,
        images: productionFormData.images
      };

      if (editingProductionItem) {
        const updated = await productionItemsAPI.update(editingProductionItem.id, itemData);
        setProductionItems(productionItems.map(item => item.id === editingProductionItem.id ? updated : item));
        toast.success('Produit modifié avec succès !');
      } else {
        const newItem = await productionItemsAPI.create(itemData);
        setProductionItems([...productionItems, newItem]);
        toast.success('Produit ajouté avec succès !');
      }
      
      resetProductionForm();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    }
  };

  const handleEditProductionItem = (item) => {
    setEditingProductionItem(item);
    setProductionFormData({
      name: item.name,
      description: item.description,
      dimensions: item.dimensions || '',
      min_quantity: item.min_quantity?.toString() || '1',
      price: item.price.toString(),
      images: item.images && item.images.length > 0 ? item.images : [],
      featured: item.featured || false
    });
    setShowProductionForm(true);
  };

  const handleDeleteProductionItem = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await productionItemsAPI.delete(id);
        setProductionItems(productionItems.filter(item => item.id !== id));
        toast.success('Produit supprimé avec succès !');
      } catch (error) {
        toast.error('Erreur lors de la suppression');
        console.error(error);
      }
    }
  };

  const resetProductionForm = () => {
    setProductionFormData({
      name: '',
      description: '',
      dimensions: '',
      min_quantity: '1',
      price: '',
      images: [],
      featured: false
    });
    setEditingProductionItem(null);
    setShowProductionForm(false);
    setDragActive(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'textile',
      price: '',
      description: '',
      images: [],
      featured: false,
      min_quantity: '1',
      quantity_available: '0',
      dimensions: ''
    });
    setIsEditing(false);
    setEditingProduct(null);
    setShowProductForm(false);
    setDragActiveProduct(false);
  };

  return (
    <div className="admin">
      <div className="admin-header">
        <div className="container">
          <h1 className="heading-secondary">Panneau d'Administration</h1>
          <div className="admin-tabs">
            <button
              className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('products');
                setShowProductForm(false);
                setShowProductionForm(false);
                resetForm();
              }}
            >
              Produits
            </button>
            <button
              className={`tab-btn ${activeTab === 'production' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('production');
                setShowProductForm(false);
                setShowProductionForm(false);
                resetForm();
                resetProductionForm();
              }}
            >
              Produits Production
            </button>
          </div>
        </div>
      </div>

      <div className="admin-content section-padding">
        <div className="container">
          {activeTab === 'products' ? (
            // Products Section
            <>
              <div className="admin-actions">
                <button
                  className="btn-primary"
                  onClick={() => setShowProductForm(prev => !prev)}
                >
                  <Plus size={20} />
                  Ajouter un produit
                </button>
              </div>
              
          {/* Product Form */}
          {showProductForm && (
            <div className="product-form-wrapper premium-card fade-in-up">
              <div className="form-header">
                <h2 className="heading-tertiary">
                  {isEditing ? 'Modifier le produit' : 'Nouveau produit'}
                </h2>
                <button className="close-btn" onClick={resetForm}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="product-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Nom du produit *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Ex: Polo Premium Coton"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="category">Catégorie *</label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                    >
                      {categories.filter(c => c.id !== 'all').map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="price">Prix (€) *</label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                      step="0.01"
                      min="0"
                      placeholder="Ex: 45.99"
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label htmlFor="featured" className="checkbox-label">
                      <input
                        type="checkbox"
                        id="featured"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleInputChange}
                      />
                      <span>Produit tendance</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Images du produit *</label>
                  <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                    Glissez-déposez vos images ou cliquez pour sélectionner (max 5MB par image)
                  </small>
                  
                  {/* Drop Zone */}
                  <div
                    onDragEnter={handleProductDrag}
                    onDragLeave={handleProductDrag}
                    onDragOver={handleProductDrag}
                    onDrop={handleProductDrop}
                    onClick={() => productFileInputRef.current?.click()}
                    style={{
                      border: `2px dashed ${dragActiveProduct ? 'var(--primary-orange)' : '#ccc'}`,
                      borderRadius: '12px',
                      padding: '2rem',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: dragActiveProduct ? 'rgba(232, 93, 47, 0.05)' : '#fafafa',
                      transition: 'all 0.2s ease',
                      marginBottom: '1rem'
                    }}
                  >
                    <input
                      ref={productFileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleProductFileSelect}
                      style={{ display: 'none' }}
                    />
                    {uploadingProductImages ? (
                      <div style={{ color: 'var(--primary-orange)' }}>
                        <div className="spinner" style={{ margin: '0 auto 0.5rem' }}></div>
                        Upload en cours...
                      </div>
                    ) : (
                      <>
                        <Upload size={40} style={{ color: dragActiveProduct ? 'var(--primary-orange)' : '#999', marginBottom: '0.5rem' }} />
                        <p style={{ color: '#666', margin: 0 }}>
                          {dragActiveProduct ? 'Déposez les images ici...' : 'Glissez vos images ici ou cliquez pour parcourir'}
                        </p>
                        <small style={{ color: '#999' }}>PNG, JPG, WEBP acceptés</small>
                      </>
                    )}
                  </div>

                  {/* Image previews with delete */}
                  {formData.images.length > 0 && (
                    <div style={{ 
                      display: 'flex', 
                      gap: '0.75rem', 
                      flexWrap: 'wrap', 
                      padding: '1rem',
                      backgroundColor: '#f8f8f8',
                      borderRadius: '8px'
                    }}>
                      {formData.images.map((imageUrl, index) => (
                        <div 
                          key={index} 
                          style={{ 
                            position: 'relative',
                            width: '100px',
                            height: '100px'
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={`Image ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '2px solid #ddd'
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveProductImage(index)}
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              backgroundColor: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}
                            title="Supprimer cette image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {formData.images.length === 0 && (
                    <p style={{ color: '#999', fontSize: '0.9rem', textAlign: 'center', margin: '0.5rem 0' }}>
                      <Image size={16} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />
                      Aucune image ajoutée
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description *</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    placeholder="Description du produit..."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="min_quantity">Quantité minimum d'achat *</label>
                    <input
                      type="number"
                      id="min_quantity"
                      name="min_quantity"
                      value={formData.min_quantity}
                      onChange={handleInputChange}
                      required
                      min="1"
                      placeholder="Ex: 10"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="quantity_available">Quantité disponible en stock</label>
                    <input
                      type="number"
                      id="quantity_available"
                      name="quantity_available"
                      value={formData.quantity_available}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="Ex: 500"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="dimensions">Dimensions</label>
                  <input
                    type="text"
                    id="dimensions"
                    name="dimensions"
                    value={formData.dimensions}
                    onChange={handleInputChange}
                    placeholder="Ex: 30x20x10 cm"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={resetForm}>
                    Annuler
                  </button>
                  <button type="submit" className="btn-primary">
                    <Save size={20} />
                    {isEditing ? 'Enregistrer' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Products List */}
          <div className="products-list">
            <h2 className="list-title">Produits ({products.length})</h2>
            <div className="products-table-wrapper">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Nom</th>
                    <th>Catégorie</th>
                    <th>Prix</th>
                    <th>Tendance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id}>
                      <td>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {product.images && product.images.length > 0 ? (
                            product.images.slice(0, 3).map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`${product.name} ${idx + 1}`}
                                className="table-image"
                                style={{ width: '40px', height: '40px' }}
                              />
                            ))
                          ) : product.image ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              className="table-image"
                            />
                          ) : (
                            <span style={{ color: '#999' }}>Aucune image</span>
                          )}
                          {product.images && product.images.length > 3 && (
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>+{product.images.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="product-name-cell">{product.name}</td>
                      <td>
                        <span className="category-badge">{product.category}</span>
                      </td>
                      <td className="price-cell">{product.price.toFixed(2)} €</td>
                      <td>
                        {product.featured && (
                          <span className="featured-badge">Oui</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn edit-btn"
                            onClick={() => handleEdit(product)}
                            title="Modifier"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDelete(product.id)}
                            title="Supprimer"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </>
          ) : (
            // Production Items Section
            <>
              <div className="admin-actions">
                <button
                  className="btn-primary"
                  onClick={() => {
                    setShowProductionForm(prev => !prev);
                  }}
                >
                  <Plus size={20} />
                  Ajouter un produit production
                </button>
              </div>

              {/* Production Item Form */}
              {showProductionForm && (
                <div className="product-form-wrapper premium-card fade-in-up">
                  <div className="form-header">
                    <h2 className="heading-tertiary">
                      {editingProductionItem ? 'Modifier le produit' : 'Nouveau produit production'}
                    </h2>
                    <button className="close-btn" onClick={resetProductionForm}>
                      <X size={24} />
                    </button>
                  </div>

                  <form onSubmit={handleProductionSubmit} className="product-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="production-name">Nom du produit *</label>
                        <input
                          type="text"
                          id="production-name"
                          name="name"
                          value={productionFormData.name}
                          onChange={handleProductionInputChange}
                          required
                          placeholder="Ex: T-Shirt Premium"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="production-price">Prix (€) HT *</label>
                        <input
                          type="number"
                          id="production-price"
                          name="price"
                          value={productionFormData.price}
                          onChange={handleProductionInputChange}
                          required
                          step="0.01"
                          min="0"
                          placeholder="Ex: 25.99"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="production-description">Description *</label>
                      <textarea
                        id="production-description"
                        name="description"
                        value={productionFormData.description}
                        onChange={handleProductionInputChange}
                        required
                        rows="3"
                        placeholder="Description du produit..."
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="production-min_quantity">Quantité minimum *</label>
                        <input
                          type="number"
                          id="production-min_quantity"
                          name="min_quantity"
                          value={productionFormData.min_quantity}
                          onChange={handleProductionInputChange}
                          required
                          min="1"
                          placeholder="Ex: 10"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="production-dimensions">Dimensions</label>
                        <input
                          type="text"
                          id="production-dimensions"
                          name="dimensions"
                          value={productionFormData.dimensions}
                          onChange={handleProductionInputChange}
                          placeholder="Ex: S, M, L, XL"
                        />
                      </div>
                    </div>

                    <div className="form-group checkbox-group">
                      <label htmlFor="production-featured" className="checkbox-label">
                        <input
                          type="checkbox"
                          id="production-featured"
                          name="featured"
                          checked={productionFormData.featured}
                          onChange={handleProductionInputChange}
                        />
                        <span>Produit vedette</span>
                      </label>
                    </div>

                    {/* Drag & Drop Image Upload */}
                    <div className="form-group">
                      <label>Images du produit *</label>
                      <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                        Glissez-déposez vos images ou cliquez pour sélectionner (max 5MB par image)
                      </small>
                      
                      {/* Drop Zone */}
                      <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          border: `2px dashed ${dragActive ? 'var(--primary-orange)' : '#ccc'}`,
                          borderRadius: '12px',
                          padding: '2rem',
                          textAlign: 'center',
                          cursor: 'pointer',
                          backgroundColor: dragActive ? 'rgba(232, 93, 47, 0.05)' : '#fafafa',
                          transition: 'all 0.2s ease',
                          marginBottom: '1rem'
                        }}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                        {uploadingImages ? (
                          <div style={{ color: 'var(--primary-orange)' }}>
                            <div className="spinner" style={{ margin: '0 auto 0.5rem' }}></div>
                            Upload en cours...
                          </div>
                        ) : (
                          <>
                            <Upload size={40} style={{ color: dragActive ? 'var(--primary-orange)' : '#999', marginBottom: '0.5rem' }} />
                            <p style={{ color: '#666', margin: 0 }}>
                              {dragActive ? 'Déposez les images ici...' : 'Glissez vos images ici ou cliquez pour parcourir'}
                            </p>
                            <small style={{ color: '#999' }}>PNG, JPG, WEBP acceptés</small>
                          </>
                        )}
                      </div>

                      {/* Image previews with delete */}
                      {productionFormData.images.length > 0 && (
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.75rem', 
                          flexWrap: 'wrap', 
                          padding: '1rem',
                          backgroundColor: '#f8f8f8',
                          borderRadius: '8px'
                        }}>
                          {productionFormData.images.map((imageUrl, index) => (
                            <div 
                              key={index} 
                              style={{ 
                                position: 'relative',
                                width: '100px',
                                height: '100px'
                              }}
                            >
                              <img
                                src={imageUrl}
                                alt={`Image ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  border: '2px solid #ddd'
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(index)}
                                style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '-8px',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  backgroundColor: '#e74c3c',
                                  color: 'white',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }}
                                title="Supprimer cette image"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {productionFormData.images.length === 0 && (
                        <p style={{ color: '#999', fontSize: '0.9rem', textAlign: 'center', margin: '0.5rem 0' }}>
                          <Image size={16} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />
                          Aucune image ajoutée
                        </p>
                      )}
                    </div>

                    <div className="form-actions">
                      <button type="button" className="btn-secondary" onClick={resetProductionForm}>
                        Annuler
                      </button>
                      <button type="submit" className="btn-primary" disabled={uploadingImages}>
                        <Save size={20} />
                        {editingProductionItem ? 'Enregistrer' : 'Ajouter'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Production Items List */}
              <div className="products-list">
                <h2 className="list-title">Produits Production ({productionItems.length})</h2>
                {loading ? (
                  <p style={{ textAlign: 'center', padding: '2rem' }}>Chargement...</p>
                ) : productionItems.length === 0 ? (
                  <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    Aucun produit de production. Cliquez sur "Ajouter un produit production" pour commencer.
                  </p>
                ) : (
                  <div className="products-table-wrapper">
                    <table className="products-table">
                      <thead>
                        <tr>
                          <th>Images</th>
                          <th>Nom</th>
                          <th>Prix HT</th>
                          <th>Qté min</th>
                          <th>Vedette</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productionItems.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                {item.images && item.images.length > 0 ? (
                                  item.images.slice(0, 3).map((img, idx) => (
                                    <img
                                      key={idx}
                                      src={img}
                                      alt={`${item.name} ${idx + 1}`}
                                      className="table-image"
                                      style={{ width: '40px', height: '40px' }}
                                    />
                                  ))
                                ) : (
                                  <span style={{ color: '#999' }}>Aucune image</span>
                                )}
                                {item.images && item.images.length > 3 && (
                                  <span style={{ fontSize: '0.8rem', color: '#666' }}>+{item.images.length - 3}</span>
                                )}
                              </div>
                            </td>
                            <td className="product-name-cell">{item.name}</td>
                            <td className="price-cell">{item.price?.toFixed(2)} € HT</td>
                            <td>{item.min_quantity || 1}</td>
                            <td>
                              {item.featured && (
                                <span className="featured-badge">Oui</span>
                              )}
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="action-btn edit-btn"
                                  onClick={() => handleEditProductionItem(item)}
                                  title="Modifier"
                                >
                                  <Edit2 size={18} />
                                </button>
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() => handleDeleteProductionItem(item.id)}
                                  title="Supprimer"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;